import { splitByChunks } from "@clearabee/ui-sdk";
import { GetRouteQuery } from "lib/routes";
import { z } from "zod";

export type QueryParams = GetRouteQuery<"ServicePage">;

export const types = [
  "BAGCOLLECTIONSKIPBAGS",
  "BAGONLYSKIPBAGS",
  "COLLECTIONONLYSKIPBAGS",
] as const;

export const locationOptions = [
  "Within 4 metres",
  "Between 4 and 10 metres",
] as const;

export const isPaidOptions = ["Yes", "No"] as const;

export const queryToState = ({
  products,
  bagContentsTypes,
  bagContentsNotes,
  readyToCollect,
  type,
  isPaid,
  distanceFromRoad,
  ref,
  postcode,
}: QueryParams): FormState => {
  const chunks = splitByChunks((products ?? "").split(","), 2);
  const obj: Record<string, number> = {};

  for (const chunk of chunks) {
    const productSku = chunk[0];
    const quantity = Number(chunk[1]);

    if (!isNaN(quantity)) {
      obj[productSku] = quantity;
    }
  }

  const bagContents = bagContentsTypes
    ? {
        types: JSON.parse(bagContentsTypes),
        notes: bagContentsNotes,
      }
    : undefined;

  return {
    refAndPostcode:
      ref || postcode
        ? { ref: ref || "", postcode: postcode || "" }
        : undefined,
    type: type as typeof types[number],
    isPaid: isPaid as typeof isPaidOptions[number],
    distanceFromRoad: distanceFromRoad as typeof locationOptions[number],
    products: obj,
    bagContents,
    readyToCollect:
      readyToCollect === undefined ? undefined : readyToCollect === "1",
  };
};

export const stateToQuery = ({
  products,
  bagContents,
  readyToCollect,
  refAndPostcode,
  ...rest
}: FormState): QueryParams => {
  return {
    ...rest,
    products: Object.entries(products ?? {})
      .flatMap(([key, value]) => (value ? `${key},${value}` : []))
      .join(","),
    bagContentsTypes: bagContents?.types
      ? JSON.stringify(bagContents.types)
      : undefined,
    bagContentsNotes: bagContents?.notes,
    readyToCollect:
      readyToCollect === undefined ? undefined : readyToCollect ? "1" : "0",
    ref: refAndPostcode?.ref,
    postcode: refAndPostcode?.postcode,
  };
};

export const validationSchema = z.object({
  type: z.enum(types).optional(),
  isPaid: z.enum(isPaidOptions).optional(),
  refAndPostcode: z
    .object({
      ref: z.string().min(1),
      postcode: z.string().min(1),
    })
    .optional(),
  bagContents: z
    .object({
      types: z.string().array().min(1),
      notes: z.string().optional(),
    })
    .optional(),
  distanceFromRoad: z.enum(locationOptions).optional(),
  readyToCollect: z.boolean().optional(),
  products: z.record(z.number().default(0)).optional(),
});

export type FormState = z.input<typeof validationSchema>;
